import { useEffect, useRef } from 'react';
import { DOC, REGION_COLORS, REGION_LABELS, REGION_LINES, type Region } from './document';

const ACCENT = '#FF7A45';
const MUTED = 'rgba(232,232,234,0.45)';
const RULE = '#26272A';
const PAPER = '#0F0F11';

const ACCENT_DEEP = '#E35B22';
const CYCLE = 13;
const BEAM_DUR = 0.88; // beam descends over 88% of cycle; last 12% holds final state
const SKEW_DEG = -1.2;

// JSON output — pre-split per line and tagged with the source region so each
// streamed character can light up the region it came from. Every entry has a
// clear counterpart on the source side; the demo claim is "what you see on the
// left, you get on the right".
type JsonLine = { region: Region; text: string };
const JSON_LINES: JsonLine[] = [
  { region: 'header', text: '{' },
  { region: 'header', text: '  "dokument": "arztbrief",' },
  { region: 'header', text: '  "klinik":   "Charité — Universitätsmedizin Berlin",' },
  { region: 'header', text: '  "campus":   "Mitte",' },
  { region: 'header', text: '  "station":  "Innere Medizin · 24b",' },
  { region: 'patient', text: '  "patient": {' },
  { region: 'patient', text: '    "name": "Meier, Johanna",' },
  { region: 'patient', text: '    "geb":  "1958-11-03"' },
  { region: 'patient', text: '  },' },
  { region: 'patient', text: '  "versicherung": {' },
  { region: 'patient', text: '    "kasse": "AOK",' },
  { region: 'patient', text: '    "art":   "GKV",' },
  { region: 'patient', text: '    "nr":    "A123456789"' },
  { region: 'patient', text: '  },' },
  { region: 'admin', text: '  "aufenthalt": {' },
  { region: 'admin', text: '    "aufnahme":   "2026-05-14",' },
  { region: 'admin', text: '    "entlassung": "2026-05-21"' },
  { region: 'admin', text: '  },' },
  { region: 'diagnosis', text: '  "diagnosen": [' },
  {
    region: 'diagnosis',
    text: '    { "typ": "haupt", "icd": "I10.90", "text": "Essentielle Hypertonie" },',
  },
  {
    region: 'diagnosis',
    text: '    { "typ": "neben", "icd": "E11.9",  "text": "Diabetes mellitus Typ 2" }',
  },
  { region: 'diagnosis', text: '  ],' },
  { region: 'befund', text: '  "befund": { "blutdruck": "158/94 mmHg", "kontext": "in Ruhe" },' },
  { region: 'labor', text: '  "labor":  { "hba1c_pct": 7.4, "ldl_mg_dl": 142 },' },
  { region: 'medikation', text: '  "medikation": [' },
  {
    region: 'medikation',
    text: '    { "wirkstoff": "Ramipril",  "dosis": "5 mg",   "frequenz": "morgens" },',
  },
  {
    region: 'medikation',
    text: '    { "wirkstoff": "Metformin", "dosis": "850 mg", "frequenz": "2× tgl" }',
  },
  { region: 'medikation', text: '  ],' },
  { region: 'empfehlung', text: '  "empfehlung": { "wiedervorstellung_monate": 3 },' },
  {
    region: 'signatur',
    text: '  "signatur":   { "name": "Dr. med. Weber", "rolle": "Oberarzt" },',
  },
  { region: 'signatur', text: '  "compliance": "§203 StGB"' },
  { region: 'signatur', text: '}' },
];
const TOTAL_CHARS = JSON_LINES.reduce((s, l) => s + l.text.length + 1, 0);

type Props = { cycleSeconds?: number };

export default function OcrMotionB({ cycleSeconds = CYCLE }: Props) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let widthCss = 0;
    let heightCss = 0;
    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      widthCss = rect.width;
      heightCss = rect.height;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(widthCss * dpr);
      canvas.height = Math.floor(heightCss * dpr);
      canvas.style.width = `${widthCss}px`;
      canvas.style.height = `${heightCss}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);

    const start = performance.now();
    let rafId = 0;

    const fontFor = (em: string | undefined, small: boolean) => {
      if (em === 'title')
        return small
          ? '600 12px "Inter", system-ui, sans-serif'
          : '600 13px "Inter", system-ui, sans-serif';
      if (em === 'subtitle')
        return small
          ? '500 10px "Inter", system-ui, sans-serif'
          : '500 11px "Inter", system-ui, sans-serif';
      if (em === 'small')
        return small
          ? 'italic 9px "Inter", system-ui, sans-serif'
          : 'italic 10px "Inter", system-ui, sans-serif';
      return small ? '10px "Inter", system-ui, sans-serif' : '11px "Inter", system-ui, sans-serif';
    };

    const draw = (now: number) => {
      const t = (now - start) / 1000;
      const W = widthCss;
      const H = heightCss;
      ctx.clearRect(0, 0, W, H);

      // Stack the two panels vertically when the canvas is tall (mobile/portrait).
      // Side-by-side is unreadable below card width, so the wrapper uses a tall
      // aspect on mobile; this matches the layout to the canvas shape.
      const stacked = H > W * 0.6;
      const padX = stacked ? 14 : 20;
      const padY = 24;
      let docX: number, docY: number, docW: number, docH: number;
      let trX: number, trY: number, trW: number, trH: number;
      if (stacked) {
        const vGap = 22;
        const colW = W - padX * 2;
        const avail = H - padY * 2 - 22; // reserve a row for the footer
        docX = padX;
        docW = colW;
        docY = padY;
        docH = (avail - vGap) * 0.5;
        trX = padX;
        trW = colW;
        trY = docY + docH + vGap;
        trH = avail - docH - vGap;
      } else {
        const gap = 24;
        docW = (W - padX * 2 - gap) * 0.5;
        trW = W - padX * 2 - gap - docW;
        docX = padX;
        docY = padY;
        docH = H - padY * 2 - 22;
        trX = padX + docW + gap;
        trY = padY;
        trH = docH;
      }

      // Transcript panel — drawn first; the skewed source page will overlay it.
      ctx.fillStyle = 'rgba(0,0,0,0.35)';
      ctx.fillRect(trX, trY, trW, trH);
      ctx.strokeStyle = RULE;
      ctx.lineWidth = 1;
      ctx.strokeRect(trX + 0.5, trY + 0.5, trW - 1, trH - 1);

      const innerX = docX + 18;
      const innerY = docY + 22;
      const innerW = docW - 36;
      const innerH = docH - 44;

      // Skew the source page (subtle scanned-document feel). Everything until
      // ctx.restore() further down draws into this rotated frame.
      ctx.save();
      const skewCx = docX + docW / 2;
      const skewCy = docY + docH / 2;
      ctx.translate(skewCx, skewCy);
      ctx.rotate((SKEW_DEG * Math.PI) / 180);
      ctx.translate(-skewCx, -skewCy);

      // Drop shadow + page.
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(docX + 4, docY + 4, docW, docH);
      ctx.fillStyle = PAPER;
      ctx.fillRect(docX, docY, docW, docH);
      ctx.strokeStyle = RULE;
      ctx.lineWidth = 1;
      ctx.strokeRect(docX + 0.5, docY + 0.5, docW - 1, docH - 1);

      // Line positions — spread evenly to fill the panel, with relative weights
      // per emphasis so the title still gets more room than body lines.
      const weights = DOC.map((l) =>
        l.emphasis === 'title' ? 1.45 : l.emphasis === 'subtitle' ? 1.15 : 1,
      );
      const weightSum = weights.reduce((a, b) => a + b, 0);
      const unit = (innerH - 8) / weightSum;
      const lineYs: number[] = [];
      const lineHs: number[] = [];
      let yCursor = innerY + 4;
      for (let i = 0; i < DOC.length; i++) {
        lineYs.push(yCursor);
        const h = weights[i]! * unit;
        lineHs.push(h);
        yCursor += h;
      }

      // Region boxes (group consecutive same-region lines).
      type RegionBox = { region: Region; y: number; h: number };
      const boxes: RegionBox[] = [];
      let curRegion: Region | null = null;
      let curStart = 0;
      for (let i = 0; i <= DOC.length; i++) {
        const r = i < DOC.length ? DOC[i]!.region : null;
        if (r !== curRegion) {
          if (curRegion !== null) {
            const top = lineYs[curStart]! - 4;
            const bot = lineYs[i - 1]! + lineHs[i - 1]! - 2;
            boxes.push({ region: curRegion, y: top, h: bot - top });
          }
          curRegion = r;
          curStart = i;
        }
      }

      // Scan beam — descends across the doc over BEAM_DUR of the cycle.
      // The same progress drives region reveal, text reveal, and JSON char
      // streaming, so the beam's Y position is "where extraction is happening".
      const cyc = reduced ? 1 : (t / cycleSeconds) % 1;
      const beamProg = Math.min(1, cyc / BEAM_DUR);
      const lastLineY = lineYs.at(-1)! + lineHs.at(-1)!;
      const beamY = innerY + beamProg * (lastLineY - innerY);

      // Document text — faint ahead of beam, full behind, medium under.
      DOC.forEach((line, i) => {
        const ly = lineYs[i]!;
        const lh = lineHs[i]!;
        const passed = ly + lh * 0.5 < beamY;
        const ahead = ly - lh * 0.5 > beamY;
        const alpha = passed ? 0.92 : ahead ? 0.22 : 0.6;
        ctx.fillStyle =
          line.emphasis === 'title' ? `rgba(255,255,255,${alpha})` : `rgba(232,232,234,${alpha})`;
        ctx.font = fontFor(line.emphasis, stacked);
        ctx.textBaseline = 'top';
        ctx.fillText(line.text, innerX, ly);
      });

      // JSON char cursor is tied to beam progress so output streams in sync.
      const charsRevealed = Math.floor(beamProg * TOTAL_CHARS);

      // Region boxes — reveal as the beam crosses each region's top edge,
      // then settle to a stable held state. `just` is clamped to [0,1]:
      // canvas silently ignores out-of-range globalAlpha assignments, so any
      // negative value would leak the previous alpha (~1) and cause a one-
      // frame full-opacity flash right as the beam arrives.
      boxes.forEach((b) => {
        if (beamY < b.y) return; // beam hasn't reached this region yet
        const just = Math.max(0, Math.min(1, (beamY - b.y) / Math.max(8, b.h * 0.35)));
        const color = REGION_COLORS[b.region];

        ctx.strokeStyle = color;
        ctx.globalAlpha = just * 0.7;
        ctx.lineWidth = 1;
        ctx.strokeRect(innerX - 6, b.y, innerW + 12, b.h);
        ctx.globalAlpha = 1;

        // Region tag chip — skipped when stacked (mobile): the narrow panel can't
        // fit a chip beside long lines without overlapping them. The colored
        // region box alone still conveys the detection.
        if (!stacked) {
          const label = REGION_LABELS[b.region];
          ctx.font = '600 9px "JetBrains Mono", ui-monospace, monospace';
          const lw = ctx.measureText(label).width;
          const chipW = lw + 10;
          const chipH = 12;
          const chipX = innerX + innerW + 12 - chipW;
          const chipY = b.y + 2;
          ctx.globalAlpha = just * 0.85;
          ctx.fillStyle = color;
          ctx.fillRect(chipX, chipY, chipW, chipH);
          ctx.fillStyle = '#0B0B0C';
          ctx.fillText(label, chipX + 5, chipY + 2);
          ctx.globalAlpha = 1;
        }
      });

      // Scan beam — drawn over the doc inside the skewed frame.
      if (!reduced && beamProg < 1) {
        const grad = ctx.createLinearGradient(0, beamY - 24, 0, beamY + 24);
        grad.addColorStop(0, 'rgba(255,122,69,0)');
        grad.addColorStop(0.5, 'rgba(255,122,69,0.28)');
        grad.addColorStop(1, 'rgba(255,122,69,0)');
        ctx.fillStyle = grad;
        ctx.fillRect(docX, beamY - 24, docW, 48);
        ctx.strokeStyle = ACCENT_DEEP;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(docX, beamY);
        ctx.lineTo(docX + docW, beamY);
        ctx.stroke();
      }

      ctx.restore(); // end skew transform

      // Transcript: render revealed JSON.
      const trPadX = 16;
      const trPadY = 22;
      ctx.font = stacked
        ? '10px "JetBrains Mono", ui-monospace, monospace'
        : '11px "JetBrains Mono", ui-monospace, monospace';
      ctx.textBaseline = 'top';
      let printed = 0;
      let ty = trY + trPadY;
      for (const jl of JSON_LINES) {
        const len = jl.text.length;
        const remaining = charsRevealed - printed;
        if (remaining <= 0) break;
        const showLen = Math.min(len, remaining);
        const shown = jl.text.slice(0, showLen);

        // Color the region's dot at the left margin.
        ctx.fillStyle = REGION_COLORS[jl.region];
        ctx.globalAlpha = 0.7;
        ctx.fillRect(trX + 8, ty + 5, 3, 3);
        ctx.globalAlpha = 1;

        // Syntax-ish coloring: keys orange, strings ink, numbers/punctuation muted.
        renderJsonLine(ctx, shown, trX + trPadX, ty);
        // Cursor at the tail of the line being typed.
        if (showLen < len && !reduced) {
          const w = ctx.measureText(shown).width;
          const blink = Math.floor(t * 2) % 2 === 0;
          if (blink) {
            ctx.fillStyle = ACCENT;
            ctx.fillRect(trX + trPadX + w + 1, ty + 1, 6, 12);
          }
        }
        printed += len + 1;
        ty += stacked ? 13 : 14;
        if (ty > trY + trH - 24) break;
      }

      // Labels.
      ctx.fillStyle = MUTED;
      ctx.font = '10px "JetBrains Mono", ui-monospace, monospace';
      ctx.fillText('SCAN · ARZTBRIEF.PDF', docX, docY - 10);
      ctx.fillText('EXTRACT · application/json', trX, trY - 10);
      const phaseLabel = beamProg < 1 ? 'SCANNING' : 'DONE';
      ctx.fillStyle = ACCENT;
      const lw = ctx.measureText(phaseLabel).width;
      ctx.fillText(phaseLabel, trX + trW - lw, trY - 10);

      // Footer — below whichever panel sits lowest (transcript when stacked).
      const fY = (stacked ? trY + trH : docY + docH) + 6;
      ctx.fillStyle = MUTED;
      ctx.font = stacked
        ? '9px "JetBrains Mono", ui-monospace, monospace'
        : '10px "JetBrains Mono", ui-monospace, monospace';
      ctx.fillText(
        `regions: ${REGION_LINES.length}   confidence: per word + field   audit: signed`,
        docX,
        fY,
      );

      if (!reduced) rafId = requestAnimationFrame(draw);
    };

    if (reduced) draw(start + cycleSeconds * 1000);
    else rafId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [cycleSeconds]);

  return (
    <div ref={wrapRef} className="relative aspect-[3/5] w-full bg-black sm:aspect-[16/7]">
      <canvas ref={canvasRef} className="block h-full w-full" />
    </div>
  );
}

// Minimal JSON-ish renderer that colors keys vs values vs punctuation.
function renderJsonLine(ctx: CanvasRenderingContext2D, text: string, x: number, y: number) {
  const KEY = '#FF7A45';
  const STR = '#E8E8EA';
  const NUM = '#34D399';
  const PUNCT = 'rgba(232,232,234,0.45)';

  // Tokenize: leading whitespace, "key": value-or-rest.
  const wsMatch = text.match(/^\s*/);
  const ws = wsMatch ? wsMatch[0] : '';
  let cx = x + ctx.measureText(ws).width;
  let rest = text.slice(ws.length);

  // Key?
  const keyMatch = rest.match(/^"([^"]*)"\s*:\s*/);
  if (keyMatch) {
    ctx.fillStyle = KEY;
    ctx.fillText(`"${keyMatch[1]}"`, cx, y);
    cx += ctx.measureText(`"${keyMatch[1]}"`).width;
    ctx.fillStyle = PUNCT;
    const colonPart = keyMatch[0].slice(keyMatch[1]!.length + 2);
    ctx.fillText(colonPart, cx, y);
    cx += ctx.measureText(colonPart).width;
    rest = rest.slice(keyMatch[0].length);
  }

  // Remaining tokens.
  const tokenRe = /"([^"]*)"|(-?\d+(?:\.\d+)?)|([{}[\],])|(\s+)|([^"{}[\],\s]+)/g;
  let m: RegExpExecArray | null;
  while ((m = tokenRe.exec(rest))) {
    const [tok, str, num, punct, _ws, other] = m;
    let color = STR;
    if (str !== undefined) color = STR;
    else if (num !== undefined) color = NUM;
    else if (punct !== undefined) color = PUNCT;
    else if (other !== undefined) color = STR;
    ctx.fillStyle = color;
    ctx.fillText(tok, cx, y);
    cx += ctx.measureText(tok).width;
  }
}
